import DeleteIcon from "@atlaskit/icon/core/delete";
import EditIcon from "@atlaskit/icon/core/edit";
import EyeOpenStrikethroughIcon from "@atlaskit/icon/core/eye-open-strikethrough";
import EyeOpenIcon from "@atlaskit/icon/core/eye-open";
import {useState} from "react";
import {NoteAttachmentTile} from "~/components/notes/noteAttachmentTile";
import {NoteMarkdownPreview} from "~/components/notes/noteMarkdownPreview";
import {WorkspaceUserAnankeAvatar} from "~/components/workspaceUserAnankeAvatar";
import {
    computeNotePlainTextLength,
    isNoteEdited,
    type NoteListItem,
} from "~/domainModels/notes";
import {AnankeIconButton} from "@vesta/ananke/components/anankeIconButton";
import {AnankeText} from "@vesta/ananke/components/anankeText";
import {formatDateTime} from "~/utilities/utilities";

const TRUNCATE_PLAIN_LENGTH = 120;

export function NoteFeedItem({
    item,
    canMutate,
    isEditing,
    onStartEdit,
    onCancelEdit,
    editComposer,
    onDelete,
    truncateEnabled = true,
}: {
    item: NoteListItem;
    canMutate: boolean;
    isEditing: boolean;
    onStartEdit: () => void;
    onCancelEdit: () => void;
    editComposer: React.ReactNode;
    onDelete: () => void;
    truncateEnabled?: boolean;
}) {
    const plainLength = item.bodyPlainTextLength || computeNotePlainTextLength(item.bodyMarkdown);
    const shouldTruncate = truncateEnabled === true && plainLength > TRUNCATE_PLAIN_LENGTH;
    const [isExpanded, setIsExpanded] = useState(false);
    const isTruncatedView = shouldTruncate === true && isExpanded === false;
    const showEditedRemark = isNoteEdited(item);

    if (isEditing === true) {
        return (
            <div className="grid gap-ds-space-150 border border-ds-border rounded-ds-border-radius-100 p-ds-space-150">
                {editComposer}
                <div className="grid justify-items-end">
                    <button
                        type="button"
                        className="border-0 bg-transparent p-0 text-ds-text-subtle text-ds-font-body-small"
                        onClick={onCancelEdit}
                    >
                        Cancel edit
                    </button>
                </div>
            </div>
        );
    }

    const showActions = shouldTruncate === true || canMutate === true;

    const actionButtons = showActions === true ? (
        <div className="grid grid-flow-col auto-cols-max items-start gap-ds-space-050 shrink-0">
            {shouldTruncate === true ? (
                <AnankeIconButton
                    type="button"
                    appearance="subtle"
                    isSelected={isExpanded === true}
                    icon={isExpanded === true ? EyeOpenStrikethroughIcon : EyeOpenIcon}
                    label={isExpanded === true ? "Collapse note" : "Expand note"}
                    onClick={() => setIsExpanded((prev) => !prev)}
                />
            ) : null}
            {canMutate === true ? (
                <>
                    <AnankeIconButton
                        type="button"
                        appearance="subtle"
                        icon={EditIcon}
                        label="Edit note"
                        onClick={onStartEdit}
                    />
                    <AnankeIconButton
                        type="button"
                        appearance="subtle"
                        icon={DeleteIcon}
                        label="Delete note"
                        onClick={onDelete}
                    />
                </>
            ) : null}
        </div>
    ) : null;

    return (
        <div className="grid grid-cols-[max-content_minmax(0,1fr)] items-start gap-x-ds-space-100">
            <WorkspaceUserAnankeAvatar
                size="small"
                name={item.author.displayName}
                src={item.author.avatarUrl}
            />
            <div className="grid min-w-0 gap-ds-space-050">
                <div
                    className={
                        showActions === true
                            ? isTruncatedView === true
                                ? "grid min-w-0 grid-cols-[minmax(0,1fr)_max-content] items-center gap-ds-space-050"
                                : "grid min-w-0 grid-cols-[minmax(0,1fr)_max-content] items-start gap-ds-space-100"
                            : "min-w-0"
                    }
                >
                    <NoteMarkdownPreview
                        markdown={item.bodyMarkdown}
                        truncate={isTruncatedView}
                    />
                    {actionButtons}
                </div>
                <div className="grid gap-ds-space-025">
                    <AnankeText size="small" color="color.text.subtlest">
                        {item.sourceLabel} · {item.author.displayName}
                        {item.author.roleLabel !== "" ? ` (${item.author.roleLabel})` : ""} ·{" "}
                        {formatDateTime(item.createdAt)}
                    </AnankeText>
                    {showEditedRemark === true ? (
                        <AnankeText size="small" color="color.text.subtlest">
                            Edited · {formatDateTime(item.updatedAt)}
                        </AnankeText>
                    ) : null}
                    {item.attachments.length > 0 ? (
                        <div className="grid grid-flow-col auto-cols-max gap-ds-space-100 overflow-x-auto">
                            {item.attachments.map((attachment) => (
                                <NoteAttachmentTile key={attachment.assetId} attachment={attachment} />
                            ))}
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
